<script setup>

import {computed, onMounted, ref, watch} from "vue";
import ToggleSwitch from "../forms/toggleSwitch.vue";
import ContainerWithTooltip from "../tooltips/containerWithTooltip.vue";

const emit = defineEmits(['updateConditions'])

const props = defineProps({
    block: {
        type: Object,
        required: true,
    },
    blockIndex: {
        type: Number,
        required: true,
    },
    blocks: {
        type: Object,
        required: true,
    },
    conditions: {
        type: Object,
        required: true,
    }
})

const _conditions = ref({
    or: [],
})
const conditionalDisplayActive = ref(false)

const toggleAlternative = (inputUuid, alternative) => {
    let altObj = {
        inputUuid: inputUuid,
        alternative: alternative.title
    }

    if (! _conditions.value.or) {
        _conditions.value.or = []
    }

    if (_conditions.value.or.find(_altObj => JSON.stringify(_altObj) === JSON.stringify(altObj))) {
        _conditions.value.or = _conditions.value.or.filter(_altObj => JSON.stringify(_altObj) !== JSON.stringify(altObj))
    } else {
        _conditions.value.or.push(altObj)
    }

    emit('updateConditions', _conditions.value)
}

const clearConditions = () => {
    _conditions.value.or = []
    emit('updateConditions', _conditions.value)
}

const conditionableBlocks = computed(() => {
    const conditionableTypes = ['forms-input-radio', 'forms-input-checkbox', 'forms-input-select', 'forms-input-score']

    let conditionableBlocksWithIndex = {};

    props.blocks.forEach((_block, index) => {
        if (conditionableTypes.includes(_block.type) && _block.uuid !== props.block.uuid) {
            let cb = _block
            cb.index = index
            conditionableBlocksWithIndex[_block.uuid] = cb
        }
    })

    return conditionableBlocksWithIndex
})

const conditionableBlocksCount = computed(() => Object.keys(conditionableBlocks.value).length)

const isScoreBlock = (block) => block.type === 'forms-input-score'

const isScoreCondition = (condition) => {
    return condition?.type === 'score' || condition?.min !== undefined || condition?.max !== undefined
}

const getScoreCondition = (inputUuid) => {
    return _conditions.value.or?.find(
        condition => condition.inputUuid === inputUuid && isScoreCondition(condition)
    )
}

const scoreConditionKey = (condition, isOr) => {
    const hasMin = condition?.min !== null && condition?.min !== undefined
    const hasMax = condition?.max !== null && condition?.max !== undefined
    const prefix = isOr ? 'form:field.conditionalDisplay.orHasScore' : 'form:field.conditionalDisplay.hasScore'

    if (hasMin && hasMax) {
        return `${prefix}Between`
    }

    if (hasMin) {
        return `${prefix}AtLeast`
    }

    return `${prefix}AtMost`
}

const updateScoreCondition = (inputUuid, key, value) => {
    let normalizedValue = value === '' || value === null || value === undefined ? null : Number(value)

    if (Number.isNaN(normalizedValue)) {
        normalizedValue = null
    }

    if (! _conditions.value.or) {
        _conditions.value.or = []
    }

    let condition = getScoreCondition(inputUuid)

    if (!condition) {
        condition = {
            inputUuid: inputUuid,
            type: 'score',
            min: null,
            max: null,
        }
        _conditions.value.or.push(condition)
    }

    condition[key] = normalizedValue

    if (condition.min === null && condition.max === null) {
        _conditions.value.or = _conditions.value.or.filter(_condition => _condition !== condition)
    }

    emit('updateConditions', _conditions.value)
}

watch(() => _conditions, (value) => {
    if (value.value.or?.length > 0) {
        conditionalDisplayActive.value = true
    }
}, {deep: true})

watch(() => conditionalDisplayActive.value, (value) => {
    if (value === false) {
        clearConditions()
    }
})

watch(() => props.conditions, (value) => {
    if (value.or) {
        _conditions.value = JSON.parse(JSON.stringify(value))
    }
}, {deep: true})

onMounted(() => {
    if (props.conditions.or) {
        _conditions.value = JSON.parse(JSON.stringify(props.conditions))
    }
})

</script>
<template>
    <div>
        <toggle-switch
            v-model:content="conditionalDisplayActive"
            :label="_mft('form:field.enableConditionalDisplay')"
        />
        <div :class="conditionalDisplayActive ? 'block' : 'hidden'">
            <p v-if="conditionalDisplayActive" class="text-xs -mt-2 text-gray-400 lg:w-4/5">{{ _mft('form:field.enableConditionalDisplay.tooltip') }}</p>
            <ul class="divide-y">
                <li
                    v-for="(block, b) in conditionableBlocks"
                    class="space-y py-2"
                >
                    <div class="grid grid-cols-5 font-bold">
                        <div class="col-span-2">
                            {{ _mft('form:field.title.label') }}
                        </div>
                        <div class="col-span-3">
                            {{ _mft('form:field.options.label') }}
                        </div>
                    </div>
                    <div class="grid grid-cols-5">
                        <div class="col-span-2">
                            {{ block.content.title }}
                        </div>
                        <div class="col-span-3">
                            <div
                                v-if="isScoreBlock(block)"
                            >
                                <div
                                    class="flex flex-wrap gap-3"
                                >
                                    <input-text
                                        inputtype="number"
                                        min="1"
                                        max="5"
                                        step="1"
                                        :label="_mft('form:field.conditionalDisplay.scoreMinLabel')"
                                        :content="getScoreCondition(block.uuid)?.min"
                                        @update:content="value => updateScoreCondition(block.uuid, 'min', value)"
                                        class="!mb-1"
                                    />
                                    <input-text
                                        inputtype="number"
                                        min="1"
                                        max="5"
                                        step="1"
                                        :label="_mft('form:field.conditionalDisplay.scoreMaxLabel')"
                                        :content="getScoreCondition(block.uuid)?.max"
                                        @update:content="value => updateScoreCondition(block.uuid, 'max', value)"
                                        class="!mb-1"
                                    />
                                </div>
                                <div class="text-xs text-gray-500 mb-1">
                                    {{ _mft('form:field.conditionalDisplay.leaveEmptyToDisable') }}
                                </div>
                            </div>
                            <ul
                                v-else
                                class="space-y-1"
                            >
                                <li
                                    v-for="(alternative, a) in block.content.alternatives"
                                    :key="a"
                                    class="flex justify-between"
                                >
                                    <div>
                                        {{ alternative.title }}
                                    </div>
                                    <div class="-mt-0.5">
                                        <button-base
                                            size="line"
                                            @click="toggleAlternative(block.uuid, alternative)"
                                        >
                                            <span v-if="_conditions.or?.find(_altObj => JSON.stringify(_altObj) === JSON.stringify({inputUuid: block.uuid, alternative: alternative.title}))">
                                                <i class="fa-regular fa-square-check text-xl"></i>
                                            </span>
                                            <span v-else>
                                                <i class="fa-regular fa-square text-xl"></i>
                                            </span>
                                        </button-base>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </li>
            </ul>

            <div class="italic">
                <div
                    v-if="conditionableBlocksCount === 0"
                >
                    {{ _mft('form:field.conditionalDisplay.noConditionableFieldsAvailable') }}
                </div>
                <p
                    v-else-if="! props.conditions.or || props.conditions.or?.length === 0"
                >
                    {{ _mft('form:field.conditionalDisplay.alwaysVisible') }}
                </p>
                <div
                    v-else
                >
                    <div class="">
                        <span>
                            {{ _mft('form:field.conditionalDisplay.onlyVisibleIf') }}
                        </span>
                        <span
                            v-for="(condition, c) in props.conditions.or"
                            :key="c"
                        >
                            <div class="flex space-x-1">
                                <template v-if="isScoreCondition(condition)">
                                    <span>
                                        {{ _mft(scoreConditionKey(condition, c > 0), {min: condition.min, max: condition.max}) }}
                                    </span>
                                </template>
                                <template v-else>
                                    <span v-if="c > 0">{{ _mft('form:field.conditionalDisplay.orHasSelected') }}</span>
                                    <span v-else>{{ _mft('form:field.conditionalDisplay.hasSelected') }}</span>
                                    <span class="font-medium">{{ condition.alternative }}</span>
                                </template>
                                <span>{{ _mft('form:field.conditionalDisplay.inField') }}</span>
                                <span class="font-medium">
                                    {{ blocks.find(_block => _block.uuid === condition.inputUuid).content.title }}
                                </span>
                            </div>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
